/**
 * AIReasoningButton - Shows AI reasoning for field extraction
 * Only visible to users with admin permission
 */

import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@roger/ui-components';
import { Sparkles, FileText, MapPin } from 'lucide-react';

// =============================================================================
// Helpers
// =============================================================================

/** Extract bare filename (e.g. "pdfName.pdf") from filename, S3 URL, or URL with query params */
function getBareFilename(filename: string): string {
  if (filename === 'N/A') {
    return 'Not found in any document';
  }
  try {
    const url = new URL(filename, 'http://dummy');
    const segment = url.pathname.split('/').pop();
    return segment ?? filename;
  } catch {
    const withoutQuery = filename.split('?')[0];
    const lastSlash = withoutQuery.lastIndexOf('/');
    return lastSlash === -1 ? withoutQuery : withoutQuery.slice(lastSlash + 1);
  }
}

// =============================================================================
// Types
// =============================================================================

export interface FieldExplanation {
  filename?: string;
  location?: string;
  explanation?: string;
}

export interface AIReasoningButtonProps {
  fieldId: string;
  fieldLabel: string;
  showAIExplanations: boolean;
  explanationData?: FieldExplanation | null;
  /** Upload name -> display name for intake documents; used to show display name for location */
  documentNameDict?: Record<string, string>;
}

// =============================================================================
// Component
// =============================================================================

export const AIReasoningButton: React.FC<AIReasoningButtonProps> = ({
  fieldId: _fieldId,
  fieldLabel,
  showAIExplanations,
  explanationData,
  documentNameDict,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const bareFilename = explanationData?.filename
    ? getBareFilename(explanationData.filename)
    : undefined;

  const displayFilename =
    bareFilename && documentNameDict
      ? (documentNameDict[bareFilename] ?? bareFilename)
      : (bareFilename ?? explanationData?.location);

  // Don't render if user is not admin
  if (!showAIExplanations) {
    return null;
  }

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              aria-label={`Show AI reasoning for ${fieldLabel}`}
              onClick={() => setIsOpen(true)}
              className='h-6 w-6 p-0 hover:bg-blue-50'
            >
              <Sparkles className='h-3.5 w-3.5 text-blue-600' />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='max-w-xs' side='top'>
            {explanationData ? (
              <div className='space-y-1.5'>
                <div className='font-medium text-xs whitespace-pre-wrap break-words'>
                  {explanationData.explanation}
                </div>
                <div className='text-xs text-gray-400 space-y-0.5 pt-1 border-t border-gray-200'>
                  <div className='flex items-start gap-1'>
                    <FileText className='h-3 w-3 shrink-0 mt-0.5' />
                    <span className='break-all whitespace-normal'>
                      {displayFilename}
                    </span>
                  </div>
                  <div className='flex items-start gap-1'>
                    <MapPin className='h-3 w-3 shrink-0 mt-0.5' />
                    <span className='break-words whitespace-normal'>
                      {explanationData.location}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className='text-xs'>No AI explanation available yet</div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='max-w-lg max-h-[70vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-base'>
              <Sparkles className='h-4 w-4 text-blue-600' />
              AI Reasoning: {fieldLabel}
            </DialogTitle>
          </DialogHeader>
          {explanationData ? (
            <div className='mt-4 space-y-4'>
              {/* Explanation - Hero Content */}
              <div className='bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100'>
                <div className='text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words w-full'>
                  {explanationData.explanation}
                </div>
              </div>

              {/* Source Information - Secondary */}
              <div className='flex flex-col gap-2.5 text-xs text-gray-600 w-full'>
                <div className='flex items-start gap-1 w-full'>
                  <FileText className='h-3.5 w-3.5 shrink-0 text-gray-400 mt-0.5' />
                  <Badge
                    variant='secondary'
                    className='font-normal text-xs whitespace-normal break-all text-left h-auto min-w-0'
                  >
                    {displayFilename}
                  </Badge>
                </div>
                <div className='flex items-start gap-1 w-full'>
                  <MapPin className='h-3.5 w-3.5 shrink-0 text-gray-400 mt-0.5' />
                  <span className='text-gray-600 break-words whitespace-normal min-w-0'>
                    {explanationData.location}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className='mt-4 py-8 text-center'>
              <Sparkles className='h-10 w-10 text-gray-300 mx-auto mb-2' />
              <p className='text-xs text-gray-500'>
                No AI explanation data available for this field.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
